// Phase 0a — read-only TanStack Query hooks for the two new contract-side
// entities surfaced by ryugu (which proxies pangaea). The response shape
// carries both `clins`/`places` and `total` so consumers can warn the user
// when ryugu truncated the page; current per-request caps are 1000 CLINs /
// 250 PoPs (clamped server-side). Phase 0b adds keyset pagination.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deliveryClient } from '../../../lib/connect/clients';
const CLINS_KEY = 'contract-clins';
const POPS_KEY = 'places-of-performance';
const REGION_OWNERS_KEY = 'region-owners';
export function useContractCLINs(programId, options = {
    onlyCurrentVersion: true
}) {
    return useQuery({
        queryKey: [
            CLINS_KEY,
            programId,
            options.onlyCurrentVersion ?? true
        ],
        queryFn: async ()=>{
            if (!programId) throw new Error('programId is required');
            return deliveryClient.listContractCLINs({
                programId,
                onlyCurrentVersion: options.onlyCurrentVersion ?? true
            });
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function usePlacesOfPerformance(programId) {
    return useQuery({
        queryKey: [
            POPS_KEY,
            programId
        ],
        queryFn: async ()=>{
            if (!programId) throw new Error('programId is required');
            return deliveryClient.listPlacesOfPerformance({
                programId
            });
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
// S11b — one owner (a program contact) per state ("region"). Read the current owners...
export function useRegionOwners(programId) {
    return useQuery({
        queryKey: [
            REGION_OWNERS_KEY,
            programId
        ],
        queryFn: async ()=>{
            if (!programId) throw new Error('programId is required');
            return deliveryClient.listRegionOwners({
                programId
            });
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
// ...and set (upsert) the owner for a state, refreshing the owners on success.
export function useSetRegionOwner(programId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (vars)=>{
            if (!programId) throw new Error('programId is required');
            return deliveryClient.setRegionOwner({
                programId,
                state: vars.state,
                ownerContactId: vars.ownerContactId,
                ownerName: vars.ownerName
            });
        },
        onSuccess: ()=>{
            void queryClient.invalidateQueries({
                queryKey: [
                    REGION_OWNERS_KEY,
                    programId
                ]
            });
        },
        // The picker mutates inline (no dialog to surface a submit error), so a
        // failed write would otherwise be swallowed and leave the row looking saved.
        // Surface it, and refetch so the trigger reverts to the persisted owner.
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to set the region owner');
            void queryClient.invalidateQueries({
                queryKey: [
                    REGION_OWNERS_KEY,
                    programId
                ]
            });
        }
    });
}
